import React, { memo, useMemo, useState } from "react";
import Visuals from "./ChatVisualsLazy";
import { STRENGTHS, parseBlocks } from "./chatMarkdownBlocks";

// The AI Assistant's answers - Markdown - as the chat window shows them. The
// subset it writes: ### headings; paragraphs, where a line break is a line
// break; **bold**, *italic*, ~~struck~~, `code` and [links](https://...);
// bulleted and numbered lists, nested by indentation; tables, with their
// column alignment; fenced code blocks, with a Copy button; > quotes; ---
// rules. And blocks of its own: ```stats - headline figures as tiles, a
// line each, "value | label", with "| alert", "| warn" or "| good" to colour
// the value; ```causes - a root cause analysis's likely causes, ranked, a
// line each, "strength | cause | evidence" (strength: strong, moderate, weak
// or unlikely - how much of the data points at it); ```chart - a small
// chart of figures the answer worked out: "type: bar|line|area|pie",
// "title: ...", "unit: ...", "series: A, B" lines, then "label | value"
// lines (drawn by ChatVisuals); and ```followups, the questions it
// suggests, which the chat window shows apart (chatAnswer.js) and is left
// out here. Built as React elements: whatever the model writes stays text,
// no HTML is ever injected, and only http(s) and mailto links are made
// links.
//
// An answer's own figures, charts and tables (built from its data on the
// server) go after its opening sentence - and its tiles, if any: afterLead.
// hideStats leaves out the ```stats tiles of an answer shown with figures of
// the server's.

// Inline: `code`, [link](url), **bold** / __bold__, ~~struck~~, *italic* /
// _italic_ (not inside snake_case words); a backslash keeps a character as
// it is.
const INLINE = new RegExp(
  [
    "(?<tick>`+)(?<code>[\\s\\S]*?[^`])\\k<tick>(?!`)",
    "\\[(?<label>[^\\]\\n]+)\\]\\((?<href>(?:[^()\\s]|\\([^()\\s]*\\))+)\\)",
    "(?<!\\\\)\\*\\*(?=\\S)(?<strong>[\\s\\S]*?\\S)\\*\\*",
    "(?<![\\\\\\w])__(?=\\S)(?<strong2>[\\s\\S]*?\\S)__(?!\\w)",
    "(?<!\\\\)~~(?=\\S)(?<del>[\\s\\S]*?\\S)~~",
    "(?<![\\\\*])\\*(?=[^\\s*])(?<em>[^*]*?[^\\s*])\\*(?!\\*)",
    "(?<![\\\\\\w])_(?=[^\\s_])(?<em2>[^_]*?[^\\s_])_(?!\\w)",
  ].join("|"),
  "g",
);
const unescape = (text) => text.replace(/\\([\\`*_{}[\]()#+\-.!|~>])/g, "$1");
const safeHref = (href) => (/^(https?:|mailto:)/i.test(href) ? href : null);

function inline(text, key) {
  const out = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(INLINE)) {
    if (match.index > last) out.push(unescape(text.slice(last, match.index)));
    const groups = match.groups;
    const k = `${key}.${(n += 1)}`;
    if (groups.tick) {
      const code = /^ .*\S.* $/.test(groups.code) ? groups.code.slice(1, -1) : groups.code;
      out.push(<code key={k}>{code}</code>);
    } else if (groups.label !== undefined) {
      const href = safeHref(groups.href);
      out.push(
        href ? (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer">
            {inline(groups.label, k)}
          </a>
        ) : (
          <span key={k}>{inline(groups.label, k)}</span>
        ),
      );
    } else if (groups.strong !== undefined || groups.strong2 !== undefined) {
      out.push(<strong key={k}>{inline(groups.strong ?? groups.strong2, k)}</strong>);
    } else if (groups.del !== undefined) {
      out.push(<del key={k}>{inline(groups.del, k)}</del>);
    } else {
      out.push(<em key={k}>{inline(groups.em ?? groups.em2, k)}</em>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(unescape(text.slice(last)));
  return out;
}

function CodeBlock({ lang, text }) {
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== "undefined" && !!navigator.clipboard;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Not allowed here (an insecure page, or permission refused).
    }
  };
  return (
    <div className="mizu-md-code">
      {lang || canCopy ? (
        <div className="mizu-md-code-head">
          <span>{lang}</span>
          {canCopy ? (
            <button type="button" className="mizu-md-copy" onClick={copy}>
              {copied ? "Copied" : "Copy"}
            </button>
          ) : null}
        </div>
      ) : null}
      <pre>
        <code>{text}</code>
      </pre>
    </div>
  );
}

function Blocks({ blocks, keyPrefix }) {
  return blocks.map((block, index) => {
    const key = `${keyPrefix}.${index}`;
    switch (block.type) {
      case "heading": {
        // In a chat bubble: # and ## no bigger than a section title.
        const Tag = `h${Math.min(6, block.level + 2)}`;
        return <Tag key={key}>{inline(block.text, key)}</Tag>;
      }
      case "code":
        return <CodeBlock key={key} lang={block.lang} text={block.text} />;
      case "stats":
        return (
          <div key={key} className={`mizu-md-stats n-${block.items.length}`}>
            {block.items.map((item, n) => (
              <div key={n} className="mizu-md-stat">
                <div
                  className={`mizu-md-stat-value${item.tone ? ` is-${item.tone}` : ""}${item.value.length > 9 ? " is-long" : ""}`}
                >
                  {inline(item.value, `${key}.${n}.v`)}
                </div>
                <div className="mizu-md-stat-label">{inline(item.label, `${key}.${n}.l`)}</div>
              </div>
            ))}
          </div>
        );
      case "causes":
        return (
          <ol key={key} className="mizu-md-causes">
            {block.items.map((item, n) => {
              const strength = STRENGTHS.get(item.strength);
              return (
                <li key={n} className={`mizu-md-cause is-${item.strength}`}>
                  <div className="mizu-md-cause-head">
                    <span className="mizu-md-cause-rank" aria-hidden="true">
                      {n + 1}
                    </span>
                    <span className="mizu-md-cause-name">{inline(item.cause, `${key}.${n}.c`)}</span>
                    <span className="mizu-md-cause-strength" title={`${strength.label} evidence`}>
                      <span className="mizu-md-cause-bars" aria-hidden="true">
                        {[1, 2, 3].map((bar) => (
                          <i key={bar} className={bar <= strength.bars ? "on" : undefined} />
                        ))}
                      </span>
                      {strength.label}
                    </span>
                  </div>
                  {item.evidence ? <div className="mizu-md-cause-evidence">{inline(item.evidence, `${key}.${n}.e`)}</div> : null}
                </li>
              );
            })}
          </ol>
        );
      case "chart":
        return <Visuals key={key} visuals={block.visuals} />;
      case "rule":
        return <hr key={key} />;
      case "quote":
        return (
          <blockquote key={key}>
            <Blocks blocks={block.children} keyPrefix={key} />
          </blockquote>
        );
      case "table":
        return (
          <div key={key} className="mizu-md-table">
            <table>
              <thead>
                <tr>
                  {block.header.map((cell, column) => (
                    <th key={column} style={block.align[column] ? { textAlign: block.align[column] } : undefined}>
                      {inline(cell, `${key}.h${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, column) => (
                      <td key={column} style={block.align[column] ? { textAlign: block.align[column] } : undefined}>
                        {inline(cell, `${key}.${r}.${column}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "list": {
        const Tag = block.ordered ? "ol" : "ul";
        return (
          <Tag key={key} start={block.ordered && block.start !== 1 ? block.start : undefined}>
            {block.items.map((children, item) => (
              <li key={item}>
                <Blocks blocks={children} keyPrefix={`${key}.${item}`} />
              </li>
            ))}
          </Tag>
        );
      }
      default:
        return (
          <p key={key}>
            {block.lines.map((line, n) => (
              <React.Fragment key={n}>
                {n ? <br /> : null}
                {inline(line, `${key}.${n}`)}
              </React.Fragment>
            ))}
          </p>
        );
    }
  });
}

function ChatMarkdown({ text, afterLead = null, hideStats = false }) {
  const parsed = useMemo(() => parseBlocks(String(text || "").replace(/\r\n?/g, "\n").replace(/\t/g, "    ").split("\n")), [text]);
  const blocks = hideStats ? parsed.filter((block) => block.type !== "stats") : parsed;
  // After the opening paragraph and the tiles that follow it - at the top
  // when the answer opens otherwise. The three parts keep their places as
  // the text streams in, so what goes between them is never drawn again.
  const at = blocks[0]?.type === "paragraph" ? (blocks[1]?.type === "stats" ? 2 : 1) : 0;
  return (
    <div className="mizu-md">
      <Blocks blocks={afterLead ? blocks.slice(0, at) : blocks} keyPrefix="b" />
      {afterLead}
      {afterLead ? <Blocks blocks={blocks.slice(at)} keyPrefix="c" /> : null}
    </div>
  );
}

export default memo(ChatMarkdown);
